/**
 * Building blocks shared by every language version of the legal documents
 * (English in `documents.tsx`, translations in `translations/<lang>.tsx`).
 */
import type { ReactNode } from "react";
import type { SiteInfo } from "../lib/siteInfo.js";

export type LegalDocumentId = "terms" | "privacy" | "refund";

export interface LegalSection {
  heading: string;
  body: ReactNode;
}

export interface LegalDocument {
  title: string;
  summary: string;
  sections: LegalSection[];
}

/** One language's full set of documents. Every language mirrors English section for section. */
export interface LegalTexts {
  terms(site: SiteInfo): LegalDocument;
  privacy(site: SiteInfo): LegalDocument;
  refund(site: SiteInfo): LegalDocument;
}

/**
 * Dodo Payments is the Merchant of Record for every purchase (reseller model).
 * Its Buyer Terms also hold its refund rules — there is no separate refund
 * policy page. The Terms must show Dodo's reseller status (Dodo merchant terms 2.1).
 */
export const MERCHANT_OF_RECORD = "Dodo Payments";
export const MOR_BUYER_TERMS = "https://dodopayments.com/legal/buyer-terms";

export function mail(address: string): ReactNode {
  // A placeholder marker is not an address — render it as text, not a mailto.
  if (!address.includes("@") || address.startsWith("[")) return <strong>{address}</strong>;
  return <a href={`mailto:${address}`}>{address}</a>;
}

export function docLink(id: LegalDocumentId, label: string): ReactNode {
  return <a href={`/${id}`}>{label}</a>;
}

export function external(href: string, label: string): ReactNode {
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {label}
    </a>
  );
}

const regionCodeByEnglishName = new Map<string, string>();
const regionNamesByLang = new Map<string, Intl.DisplayNames>();

/**
 * The seller's country (configured as an English name, e.g. "Kazakhstan") in the
 * reader's language — "Казахстан", "Kasachstan"… — via Intl.DisplayNames. Falls
 * back to the configured text when it is not a recognised country name.
 */
export function countryName(site: SiteInfo, lang: string): string {
  const configured = site.entityCountry;
  if (configured.startsWith("[")) return configured;
  try {
    if (regionCodeByEnglishName.size === 0) {
      const english = new Intl.DisplayNames(["en"], { type: "region" });
      for (let a = 65; a <= 90; a++) {
        for (let b = 65; b <= 90; b++) {
          const code = String.fromCharCode(a, b);
          const name = english.of(code);
          if (name && name !== code) regionCodeByEnglishName.set(name.toLowerCase(), code);
        }
      }
    }
    const code = regionCodeByEnglishName.get(configured.trim().toLowerCase());
    if (!code) return configured;
    let names = regionNamesByLang.get(lang);
    if (!names) {
      names = new Intl.DisplayNames([lang], { type: "region" });
      regionNamesByLang.set(lang, names);
    }
    return names.of(code) ?? configured;
  } catch {
    return configured;
  }
}
